import { z } from 'zod';

/**
 * As regras do cadastro, **portadas da `auth.entity.ts` do
 * `enamed-feedback-lp`** — mesmos campos, mesma máscara, mesmos valores do
 * contrato. O que não veio (login, sessão do Firebase, `/users/me`) volta junto
 * com o fluxo de "já tenho conta".
 */

/**
 * As especialidades que o cadastro oferece, **lista fechada** — a mesma do
 * `enamed-feedback-lp`. Fechada, e não texto livre, porque o campo serve para
 * agrupar: em texto, "Clínica médica", "clinica medica" e "CM" seriam três
 * especialidades diferentes no relatório. O back aceita até 120 caracteres; a
 * maior daqui tem 39.
 */
export const MEDICAL_SPECIALTIES = [
  'Acupuntura',
  'Alergia e imunologia',
  'Anestesiologia',
  'Cirurgia cardiovascular',
  'Cirurgia geral',
  'Clínica médica',
  'Dermatologia',
  'Genética médica',
  'Ginecologia e obstetrícia',
  'Homeopatia',
  'Infectologia',
  'Medicina de família e comunidade',
  'Medicina de Emergência',
  'Medicina do trabalho',
  'Medicina do tráfego',
  'Medicina esportiva',
  'Medicina física e reabilitação',
  'Medicina intensiva',
  'Medicina legal',
  'Medicina nuclear',
  'Medicina preventiva e social',
  'Neurocirurgia',
  'Neurologia',
  'Nutrologia',
  'Oftalmologia',
  'Ortopedia e traumatologia',
  'Otorrinolaringologia',
  'Patologia',
  'Patologia Clínica/Medicina Laboratorial',
  'Pediatria',
  'Psiquiatria',
  'Radiologia e Diagnóstico por imagem',
  'Radioterapia',
] as const;

export type MedicalSpecialty = (typeof MEDICAL_SPECIALTIES)[number];

/** Duas palavras é o mínimo para haver nome e sobrenome a separar. */
export function isFullName(value: string): boolean {
  return value.trim().split(/\s+/).filter(Boolean).length >= 2;
}

/** Primeira palavra é o nome, o resto é o sobrenome. */
export function splitName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  const [firstName = '', ...rest] = fullName.trim().split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}

const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 11;

/** Comprimento da máscara cheia, `(11) 91234-5678`. */
export const PHONE_MASK_LENGTH = 15;

/**
 * Máscara enquanto a pessoa digita. Reagrupa no 11º dígito: até dez é fixo,
 * `(XX) XXXX-XXXX`; no décimo primeiro vira celular, `(XX) XXXXX-XXXX`.
 */
export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, PHONE_MAX_DIGITS);

  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  const break_ = digits.length === PHONE_MAX_DIGITS ? 7 : 6;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, break_)}-${digits.slice(break_)}`;
}

export function isValidPhone(value: string): boolean {
  return value.replace(/\D/g, '').length >= PHONE_MIN_DIGITS;
}

/** Padrão da Primum — o mesmo do `@react-apps/auth` e do ENAMED. */
export const PASSWORD_MIN_LENGTH = 8;

/** O documento que a pessoa aceita no cadastro — o mesmo do ENAMED. */
export const PRIVACY_POLICY_URL =
  'https://politicas.primum.edu.br/aristo/politicas-de-privacidade';

/**
 * O corpo do cadastro, na **forma anônima** do ENAMED: e-mail e senha no corpo,
 * e o servidor cria identidade, conta e perfil na mesma chamada — ou nenhum.
 *
 * Só a pessoa: ensaio e gabarito vão em chamadas próprias, **depois**, já com
 * sessão. Quem chega pelo gabarito dias depois pode nem ter feito o ensaio, e
 * um cadastro que carregasse os dois amarraria uma conta a um envio.
 */
export const RegistrationRequestSchema = z.object({
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  email: z.string().email().max(254),
  password: z.string().min(PASSWORD_MIN_LENGTH),
  phone: z.string().min(1).max(40),
  intendedSpecialty: z.string().min(1).max(120),
});

export type RegistrationRequest = z.infer<typeof RegistrationRequestSchema>;

/** O que o formulário de cadastro entrega, antes de virar corpo. */
export interface SignUpInput {
  fullName: string;
  email: string;
  password: string;
  phone: string;
  intendedSpecialty: MedicalSpecialty;
}

/**
 * Do formulário para o corpo da rota. **Só estes campos**: o back recusa campo
 * a mais com 400, e nem o aceite dos termos (vai ao serviço de políticas) nem
 * UTM (fica no GTM) passam por ele.
 */
export function toRegistrationRequest(input: SignUpInput): RegistrationRequest {
  const { firstName, lastName } = splitName(input.fullName);

  return RegistrationRequestSchema.parse({
    firstName,
    lastName,
    email: input.email.trim(),
    password: input.password,
    phone: input.phone,
    intendedSpecialty: input.intendedSpecialty,
  });
}

/**
 * O que o formulário curto de "completar cadastro" coleta — o cadastro desta
 * plataforma para quem **já tem conta** na Primum (outra plataforma, Google)
 * mas ainda não tem perfil aqui. Sem e-mail e sem senha: a conta já existe, e o
 * e-mail vem do token.
 */
export interface ProfileFormInput {
  fullName: string;
  phone: string;
  intendedSpecialty: MedicalSpecialty;
}

/**
 * O corpo do mesmo `POST` do cadastro, na **forma autenticada**: com o token,
 * o back amarra a conta existente, cria o perfil desta plataforma e grava o
 * formulário. E-mail e senha no corpo seriam recusados (400).
 */
export const CompleteRegistrationRequestSchema = z.object({
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  phone: z.string().min(1).max(40),
  intendedSpecialty: z.string().min(1).max(120),
});

export type CompleteRegistrationRequest = z.infer<
  typeof CompleteRegistrationRequestSchema
>;

export function toCompleteRegistrationRequest(
  input: ProfileFormInput,
): CompleteRegistrationRequest {
  const { firstName, lastName } = splitName(input.fullName);
  return CompleteRegistrationRequestSchema.parse({
    firstName,
    lastName,
    phone: input.phone,
    intendedSpecialty: input.intendedSpecialty,
  });
}

/**
 * O que o cadastro já tinha coletado quando o e-mail se revelou de uma conta
 * existente. Vai junto para o login: se a conta não tiver perfil aqui, o
 * cadastro desta plataforma se completa com estes dados, sem pedir de novo.
 */
export type ProfileDraft = ProfileFormInput;

export interface SignInInput {
  email: string;
  password: string;
}

/** Quem está logado. Só o que a tela usa. */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  /**
   * Se a conta já tem perfil **nesta plataforma**. `false` é quem entrou com
   * uma conta da Primum de outra plataforma (o `POST /auth/sign-in` devolve
   * `platformProfile: null`): ela entra, mas ensaio, gabarito e ranking
   * respondem 403 até completar o cadastro daqui.
   */
  hasProfile: boolean;
}

/**
 * O back recusou um envio ou leitura com 403: a sessão não tem perfil nesta
 * plataforma. A tela pede o cadastro curto e tenta de novo.
 */
export class ProfileRequiredError extends Error {
  constructor() {
    super('Complete seu cadastro para continuar.');
    this.name = 'ProfileRequiredError';
  }
}

/** O primeiro nome, para o "Olá, Maria" do painel. */
export function firstNameOf(user: AuthUser): string {
  return splitName(user.name).firstName || user.email.split('@')[0];
}

/** E-mail e senha não batem (ou a conta não existe — o back não distingue). */
export class InvalidCredentialsError extends Error {
  constructor() {
    super('E-mail ou senha incorretos.');
    this.name = 'InvalidCredentialsError';
  }
}

/**
 * Cadastro com um e-mail que já tem conta (409 `EMAIL_ALREADY_IN_USE`): a tela
 * leva ao login, e o que a pessoa já digitou segue como `ProfileDraft`.
 */
export class ExistingAccountError extends Error {
  readonly email: string;

  constructor(email: string) {
    super('Este e-mail já tem conta.');
    this.name = 'ExistingAccountError';
    this.email = email;
  }
}
